package one.devsbox.www.ui.devices.detail

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridItemSpan
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import kotlinx.coroutines.launch
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.daysUntil
import kotlinx.datetime.TimeZone
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.Month
import kotlin.time.Clock
import kotlin.math.pow
import kotlin.math.round
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.text.style.TextAlign
import compose.assets.Svg
import kotlinx.browser.window
import compose.assets.svg.CampaignOutline
import compose.assets.svg.ImageOutline
import compose.assets.svg.ResetOutline
import compose.assets.svg.CloseOutline
import one.devsbox.shared.ads.DeviceType
import one.devsbox.www.ui.devices.label
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import one.devsbox.www.provider.OverlayProvider
import one.devsbox.www.provider.LocalDisplayProvider
import one.devsbox.www.tools.root
import tk.mallumo.compose.navigation.navTo_CmdUI
import tk.mallumo.compose.navigation.navTo_AppWheelDUI
import one.devsbox.shared.devices.ApplicationType
import compose.assets.svg.LayersOutline

@Composable
@ComposableNavNode(ArgsId::class)
fun DeviceDUI() {
    with(DeviceDCtx.get()) {
        ContentDeviceDUI()
    }
}

@Composable
context(ctx: DeviceDCtx)
private fun ContentDeviceDUI() {
    val overlayState = OverlayProvider.OverlayState.rememberState { state ->
        ScreenshotOverlayContent(state)
    }.apply {
        ctx.overlay.attach(this)
    }

    LaunchedEffect(Unit) {
        ctx.vm.onTriggerScreenshot = {
            ctx.vm.takeScreenshot()
            overlayState.show()
        }
    }

    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    SuccessUI(state.nnvl)
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }

        if (ctx.vm.showDeleteConfirmation) {
            AlertDialog(
                onDismissRequest = { ctx.action.dismissDeleteConfirmation() },
                title = { Text(stringResource(Res.string.ui_devices_detail_devicedui_delete_confirm_title)) },
                text = { Text(stringResource(Res.string.ui_devices_detail_devicedui_delete_confirm_message)) },
                confirmButton = {
                    TextButton(onClick = { ctx.action.confirmDelete() }) {
                        Text(
                            text = stringResource(Res.string.ui_devices_detail_devicedui_btn_delete),
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                },
                dismissButton = {
                    TextButton(onClick = { ctx.action.dismissDeleteConfirmation() }) {
                        Text(
                            text = stringResource(Res.string.ui_devices_detail_devicedui_btn_cancel),
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            )
        }

        if (ctx.deviceActions.showDeviceActions) {
            DeviceActionsDialog()
        }

        if (ctx.vm.showAppSelectorDialog) {
            AppSelectorDialog()
        }
    }
}

@Composable
context(ctx: DeviceDCtx)
private fun DeviceActionsDialog() {
    val data = (ctx.vm.state as? DataState.Result)?.entry
    val version = data?.device?.content?.version ?: "0.0"
    val parts = version.split(".")

    val currentRuntime = parts.getOrNull(0)?.toIntOrNull() ?: 0
    val currentWww = parts.getOrNull(1)?.toIntOrNull() ?: 0

    val latestWww = data?.latestWwwVersion ?: 0
    val showUpdateWww = currentWww < latestWww

    val latestRuntime = data?.latestRuntimeVersion ?: 0
    val showUpdateRuntime = currentRuntime < latestRuntime

    val deviceActions = ctx.deviceActions
    val scope = rememberCoroutineScope()

    AlertDialog(
        onDismissRequest = { deviceActions.close() },
        title = { Text(stringResource(Res.string.ui_devices_detail_device_actions_dialog_title)) },
        text = {
            Card(
                modifier = Modifier.fillMaxWidth(),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                colors = CardDefaults.cardColors(containerColor = Color.Transparent),
                shape = RoundedCornerShape(8.dp)
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    // Header Row
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "Type",
                            modifier = Modifier.weight(1.5f),
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.titleSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            text = "Current",
                            modifier = Modifier.weight(1f),
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.titleSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = TextAlign.Center
                        )
                        Text(
                            text = "Latest",
                            modifier = Modifier.weight(1f),
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.titleSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = TextAlign.Center
                        )
                        Text(
                            text = "Action",
                            modifier = Modifier.weight(2f),
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.titleSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = TextAlign.End
                        )
                    }

                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

                    // WWW Row
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 12.dp, vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "WWW",
                            modifier = Modifier.weight(1.5f),
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Medium
                        )
                        Text(
                            text = currentWww.toString(),
                            modifier = Modifier.weight(1f),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center
                        )
                        Text(
                            text = latestWww.toString(),
                            modifier = Modifier.weight(1f),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center
                        )
                        Box(
                            modifier = Modifier.weight(2f),
                            contentAlignment = Alignment.CenterEnd
                        ) {
                            if (showUpdateWww) {
                                if (deviceActions.isUpdatingWww) {
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(18.dp),
                                        strokeWidth = 2.dp,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                } else if (deviceActions.updateWwwStatus != null) {
                                    val status = deviceActions.updateWwwStatus!!
                                    Text(
                                        text = if (status == "Update is queued is pecessing") stringResource(
                                            Res.string.ui_devices_detail_device_actions_update_queued
                                        ) else status,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = if (status.startsWith("Failed")) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary,
                                        textAlign = TextAlign.End
                                    )
                                } else {
                                    Button(
                                        onClick = { scope.launch { deviceActions.updateWww(ctx.args.id) } },
                                        contentPadding = PaddingValues(
                                            horizontal = 16.dp,
                                            vertical = 6.dp
                                        ),
                                        modifier = Modifier.height(32.dp)
                                    ) {
                                        Text("Update", style = MaterialTheme.typography.bodyMedium)
                                    }
                                }
                            } else {
                                Text(
                                    text = "Up to date",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f)
                                )
                            }
                        }
                    }

                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

                    // Runtime Row
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 12.dp, vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "Runtime",
                            modifier = Modifier.weight(1.5f),
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Medium
                        )
                        Text(
                            text = currentRuntime.toString(),
                            modifier = Modifier.weight(1f),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center
                        )
                        Text(
                            text = latestRuntime.toString(),
                            modifier = Modifier.weight(1f),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center
                        )
                        Box(
                            modifier = Modifier.weight(2f),
                            contentAlignment = Alignment.CenterEnd
                        ) {
                            if (showUpdateRuntime) {
                                if (deviceActions.isUpdatingRuntime) {
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(18.dp),
                                        strokeWidth = 2.dp,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                } else if (deviceActions.updateRuntimeStatus != null) {
                                    val status = deviceActions.updateRuntimeStatus!!
                                    Text(
                                        text = if (status == "Update is queued is pecessing") stringResource(
                                            Res.string.ui_devices_detail_device_actions_update_queued
                                        ) else status,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = if (status.startsWith("Failed")) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary,
                                        textAlign = TextAlign.End
                                    )
                                } else {
                                    Button(
                                        onClick = { scope.launch { deviceActions.updateRuntime(ctx.args.id) } },
                                        contentPadding = PaddingValues(
                                            horizontal = 16.dp,
                                            vertical = 6.dp
                                        ),
                                        modifier = Modifier.height(32.dp)
                                    ) {
                                        Text("Update", style = MaterialTheme.typography.bodyMedium)
                                    }
                                }
                            } else {
                                Text(
                                    text = "Up to date",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f)
                                )
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    TextButton(
                        onClick = { scope.launch { deviceActions.reboot(ctx.args.id) } },
                        enabled = deviceActions.isRebootEnabled && !deviceActions.isRebooting
                    ) {
                        if (deviceActions.isRebooting) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(16.dp),
                                strokeWidth = 2.dp,
                                color = MaterialTheme.colorScheme.primary
                            )
                        } else {
                            Text(
                                text = stringResource(Res.string.ui_devices_detail_device_actions_btn_reboot),
                                color = MaterialTheme.colorScheme.error
                            )
                        }
                    }
                    deviceActions.rebootStatus?.let { status ->
                        Text(
                            text = if (status == "Reboot is queued") stringResource(Res.string.ui_devices_detail_device_actions_reboot_queued) else status,
                            style = MaterialTheme.typography.bodySmall,
                            color = if (status.startsWith("Failed")) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary
                        )
                    }
                }
                @OptIn(ExperimentalFoundationApi::class)
                Text(
                    text = "CMD",
                    color = MaterialTheme.colorScheme.secondary,
                    style = MaterialTheme.typography.labelLarge,
                    modifier = Modifier
                        .clip(RoundedCornerShape(50))
                        .combinedClickable(
                            onClick = {
                                ctx.nav.root().navTo_CmdUI(ArgsId(ctx.args.id))
                            },
                            onLongClick = {
                                val url = "${window.location.origin}?screen=cmd&id=${ctx.args.id}"
                                window.open(url, "_blank")
                            }
                        )
                        .padding(horizontal = 12.dp, vertical = 8.dp)
                )
                TextButton(onClick = { deviceActions.close() }) {
                    Text(stringResource(Res.string.ui_devices_detail_device_actions_btn_close))
                }
            }
        }
    )
}

@Composable
context(ctx: DeviceDCtx)
private fun BoxScope.ScreenshotOverlayContent(overlayState: OverlayProvider.Visibility) {
    val vm = ctx.vm
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.85f))
    ) {
        when (val sState = vm.screenshotState) {
            is DataState.Idle -> Unit
            is DataState.Loading -> {
                CircularProgressIndicator(
                    modifier = Modifier.align(Alignment.Center),
                    color = MaterialTheme.colorScheme.primary
                )
            }

            is DataState.Error -> {
                Column(
                    modifier = Modifier.align(Alignment.Center).padding(32.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Text(
                        text = sState.toString(),
                        color = Color.White,
                        style = MaterialTheme.typography.bodyLarge,
                        textAlign = TextAlign.Center
                    )
                    Button(
                        onClick = { vm.takeScreenshot() },
                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary)
                    ) {
                        Icon(imageVector = Svg.ResetOutline, contentDescription = null)
                        Spacer(Modifier.width(8.dp))
                        Text("Repeat")
                    }
                }
            }

            is DataState.Result -> {
                Image(
                    bitmap = sState.nnvl,
                    contentDescription = "Device Screenshot",
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Fit
                )

                // Repeat/Retake button on success
                FloatingActionButton(
                    onClick = { vm.takeScreenshot() },
                    shape = CircleShape,
                    containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.8f),
                    contentColor = MaterialTheme.colorScheme.onSurface,
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .padding(16.dp)
                ) {
                    Icon(
                        imageVector = Svg.ResetOutline,
                        contentDescription = "RETAKE",
                    )
                }
            }
        }

        // Close button (always visible)
        FloatingActionButton(
            onClick = {
                vm.clearScreenshot()
                overlayState.hide()
            },
            shape = CircleShape,
            containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.8f),
            contentColor = MaterialTheme.colorScheme.onSurface,
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(16.dp)
        ) {
            Icon(
                imageVector = Svg.CloseOutline,
                contentDescription = "CLOSE",
            )
        }
    }
}

@Composable
context(ctx: DeviceDCtx)
private fun SuccessUI(data: DeviceDVM.Data) {

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isWide = maxWidth >= 1000.dp

        LazyVerticalStaggeredGrid(
            columns = StaggeredGridCells.Fixed(if (isWide) 2 else 1),
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(
                start = 16.dp,
                top = 16.dp,
                end = 16.dp,
                bottom = 250.dp
            ),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalItemSpacing = 16.dp
        ) {
            // GLOBAL TITLE & ADDRESS ON TOP
            item(span = StaggeredGridItemSpan.FullLine) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = data.device.info.name.uppercase(),
                        style = MaterialTheme.typography.headlineMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    if (data.device.info.address.isNotEmpty()) {
                        Text(
                            text = data.device.info.address,
                            style = MaterialTheme.typography.titleMedium,
                            color = MaterialTheme.colorScheme.onSurface,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier
                                .align(Alignment.Bottom)
                                .weight(1F)
                        )
                    }
                }
            }

            // DEVICE VISUAL
            item {
                DeviceVisualSection(
                    modifier = Modifier.fillMaxWidth(),
                    photoId = data.device.info.photoId
                )
            }

            // TECHNICAL SPECIFICATIONS
            item {
                DeviceInfoSection(device = data.device)
            }

            // OPENING HOURS
            item {
                DeviceOpeningHoursSection(
                    device = data.device,
                    enabled = ctx.me.permission.location.read
                ) {
                    ctx.action.openLocation(data.device.info.locationId)
                }
            }

            // ACTIVE CAMPAIGNS OR APPS
            if (data.device.apps.isEmpty()) {
                item {
                    ActiveCampaignsSection(campaigns = data.campaigns)
                }
            } else {
                item {
                    DeviceAppsSection(apps = data.device.apps)
                }
            }

            // MONITORING
            item(span = StaggeredGridItemSpan.FullLine) {
                DeviceMonitoringSection()
            }
        }
    }
}

@Composable
context(ctx: DeviceDCtx)
private fun DeviceVisualSection(modifier: Modifier = Modifier, photoId: Long) {
    Column(
        modifier = modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant)
            .clickable(photoId > 0) { ctx.action.openMedia(photoId) }
    ) {
        Text(
            text = stringResource(Res.string.ui_devices_edit_deviceeui_label_visual),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary,
            modifier = Modifier.padding(16.dp)
        )


        val boxModifier = Modifier
            .fillMaxWidth()
            .aspectRatio(16F / 9F)
            .background(MaterialTheme.colorScheme.surface)
        Box(modifier = boxModifier) {
            if (photoId > 0L) {
                LazyImage(
                    id = photoId,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
            } else {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.Center,
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Icon(
                        imageVector = Svg.ImageOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    Text(
                        text = stringResource(Res.string.ui_devices_edit_deviceeui_no_image),
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: DeviceDCtx)
private fun DeviceInfoSection(device: SharedIO.Device) {
    Column(
        modifier = Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = stringResource(Res.string.ui_devices_edit_deviceeui_label_technical_specs),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary
        )
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

        SpecRow(
            label = stringResource(Res.string.ui_devices_detail_devicedui_label_online_status),
            value = stringResource(if (device.info.online) Res.string.ui_devices_detail_devicedui_status_online else Res.string.ui_devices_detail_devicedui_status_offline),
        )

        SpecRow(
            label = stringResource(Res.string.ui_devices_edit_deviceeui_label_location),
            value = device.info.nameLocation.ifEmpty { "--" },
        )
        SpecRow(
            label = stringResource(Res.string.ui_devices_detail_devicedui_label_type),
            value = stringResource((DeviceType[device.info.type] ?: DeviceType.INDOOR).label())
        )
        SpecRow(
            label = stringResource(Res.string.ui_devices_edit_deviceeui_label_orientation),
            value = if (device.content.config.screenW > device.content.config.screenH) {
                stringResource(Res.string.ui_devices_edit_deviceeui_orientation_horizontal)
            } else {
                stringResource(Res.string.ui_devices_edit_deviceeui_orientation_vertical)
            }
        )
        SpecRow(
            label = stringResource(Res.string.ui_devices_detail_devicedui_label_resolution),
            value = "${device.content.config.screenW} x ${device.content.config.screenH}"
        )
        SpecRow(
            label = stringResource(Res.string.ui_devices_edit_deviceeui_label_aspect_ratio),
            value = device.info.aspectRatio
        )
        SpecRow(
            label = stringResource(Res.string.ui_devices_detail_devicedui_label_versions),
            value = device.content.version
        )
        SpecRow(
            label = stringResource(Res.string.ui_devices_detail_devicedui_label_id),
            value = device.info.id.toString(),
            withDivider = false
        )
    }
}

@Composable
private fun DeviceOpeningHoursSection(
    device: SharedIO.Device,
    enabled: Boolean,
    onClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .border(1.dp, MaterialTheme.colorScheme.outlineVariant)
            .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier.graphicsLayer {
                alpha = 0.8f
            })
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = stringResource(Res.string.ui_devices_edit_deviceeui_label_openning_hours),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary
        )
        OpeningHours(
            openingHours = device.content.config.openingHours,
            backgroundColor = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f),
            primaryColor = MaterialTheme.colorScheme.primary,
            showTitle = false
        )
    }
}

@Composable
private fun SpecRow(
    label: String,
    value: String,
    withDivider: Boolean = true
) {
    val rowModifier = Modifier
        .padding(horizontal = 16.dp)
        .fillMaxWidth()
    Column {
        Row(
            modifier = rowModifier,
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                text = value,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
            )
        }
    }
    if (withDivider) HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
}

@Composable
context(ctx: DeviceDCtx)
private fun ActiveCampaignsSection(campaigns: SharedIO.Campaigns) {
    Column(
        modifier = Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant).padding(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = stringResource(Res.string.ui_devices_detail_devicedui_label_active_campaigns),
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.primary
            )
            if (!campaigns.items.isEmpty()) {
                Spacer(Modifier.weight(1f))
                EntryViewSwitch(ctx.prefs.campaignsInDeviceLayoutType) {
                    ctx.prefs.campaignsInDeviceLayoutType = it
                }
            }
        }
        Spacer(Modifier.height(32.dp))

        if (campaigns.items.isEmpty()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = 32.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Icon(
                    imageVector = Svg.CampaignOutline,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                    modifier = Modifier.size(48.dp)
                )
                Spacer(modifier = Modifier.height(16.dp))
                Text(
                    text = stringResource(Res.string.ui_devices_detail_devicedui_campaigns_empty),
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center
                )
            }
        } else {
            AnimatedContent(targetState = ctx.prefs.campaignsInDeviceLayoutType) { viewMode ->
                val enabled = ctx.me.permission.campaign.read
                if (viewMode == EntryViewMode.Card) {
                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        campaigns.items.forEach { campaign ->
                            Box(
                                modifier = Modifier
                                    .weight(1f, fill = false)
                                    .widthIn(min = 192.dp, max = 256.dp)
                            ) {
                                CampaignCard(campaign, enabled) {
                                    ctx.action.openCampaign(campaign.id)
                                }
                            }
                        }
                    }
                } else {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        campaigns.items.forEach { campaign ->
                            CampaignListItem(campaign, enabled) {
                                ctx.action.openCampaign(campaign.id)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CampaignCard(campaign: SharedIO.CampaignSimple, enabled: Boolean, onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth()
            .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier.graphicsLayer {
                alpha = 0.5f
            }),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        shape = RoundedCornerShape(4.dp)
    ) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(16f / 9f)
                    .background(Color.Transparent)
            ) {
                if (campaign.media.isNotEmpty()) {
                    LazyImage(
                        id = campaign.media.first(),
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(MaterialTheme.colorScheme.surfaceVariant),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            imageVector = Svg.ImageOutline,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                            modifier = Modifier.size(36.dp)
                        )
                    }
                }
            }
            Text(
                text = campaign.name.uppercase(),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.padding(16.dp)
            )
        }
    }
}

@Composable
private fun CampaignListItem(
    campaign: SharedIO.CampaignSimple,
    enabled: Boolean,
    onClick: () -> Unit
) {
    Surface(
        modifier = Modifier.fillMaxWidth()
            .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier.graphicsLayer {
                alpha = 0.5f
            }),
        color = Color.Transparent,
        shape = RoundedCornerShape(4.dp),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .width(96.dp)
                    .aspectRatio(16f / 9f)
                    .background(Color.Transparent)
            ) {
                if (campaign.media.isNotEmpty()) {
                    LazyImage(
                        id = campaign.media.first(),
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(MaterialTheme.colorScheme.surfaceVariant),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            imageVector = Svg.ImageOutline,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                            modifier = Modifier.size(24.dp)
                        )
                    }
                }
            }
            Spacer(Modifier.width(16.dp))
            Text(
                text = campaign.name.uppercase(),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                maxLines = 2,
                color = MaterialTheme.colorScheme.onSurface
            )
        }
    }
}

@Composable
context(ctx: DeviceDCtx)
private fun DeviceMonitoringSection() {
    val dp = LocalDisplayProvider.current.state
    val isSmall = dp.smallScreen

    Column(
        modifier = Modifier
            .border(1.dp, MaterialTheme.colorScheme.outlineVariant)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = stringResource(Res.string.ui_devices_detail_monitoring_title),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary
        )

        Crossfade(targetState = ctx.vm.monitoringState) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxWidth().height(100.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(modifier = Modifier.size(24.dp))
                    }
                }

                is DataState.Error -> {
                    Text(
                        text = state.toString(),
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }

                is DataState.Result -> {
                    val records = state.nnvl
                    if (records.isEmpty()) {
                        Text(
                            text = stringResource(Res.string.ui_devices_detail_monitoring_empty),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                        )
                    } else {
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            // Header Row
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 8.dp, vertical = 6.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {

                                if (isSmall) {
                                    Column(modifier = Modifier.weight(2.5f)) {
                                        Text(
                                            stringResource(Res.string.ui_devices_detail_monitoring_header_timestamp),
                                            fontWeight = FontWeight.Bold,
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                        Text(
                                            stringResource(Res.string.ui_devices_detail_monitoring_header_network),
                                            fontWeight = FontWeight.Bold,
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    }
                                } else {
                                    Text(
                                        stringResource(Res.string.ui_devices_detail_monitoring_header_timestamp),
                                        modifier = Modifier.weight(1.5f),
                                        fontWeight = FontWeight.Bold,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                    Text(
                                        stringResource(Res.string.ui_devices_detail_monitoring_header_network_speed),
                                        modifier = Modifier.weight(2f),
                                        fontWeight = FontWeight.Bold,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                                Text(
                                    stringResource(Res.string.ui_devices_detail_monitoring_header_disk),
                                    modifier = Modifier.weight(1f),
                                    fontWeight = FontWeight.Bold,
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    textAlign = TextAlign.Center
                                )
                                Text(
                                    stringResource(Res.string.ui_devices_detail_monitoring_header_ram),
                                    modifier = Modifier.weight(1f),
                                    fontWeight = FontWeight.Bold,
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    textAlign = TextAlign.Center
                                )
                                Text(
                                    stringResource(Res.string.ui_devices_detail_monitoring_header_temp),
                                    modifier = Modifier.weight(1f),
                                    fontWeight = FontWeight.Bold,
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    textAlign = TextAlign.Center
                                )
                            }
                            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

                            records.forEachIndexed { index, item ->
                                if (index != 0) HorizontalDivider(
                                    color = MaterialTheme.colorScheme.outlineVariant.copy(
                                        alpha = 0.5f
                                    )
                                )
                                val timeText = item.dt.formatMonitoringTime()
                                val netText = if (item.net.online) {
                                    val mbps = item.net.kbps.toDouble() / 1000.0
                                    stringResource(
                                        Res.string.ui_devices_detail_monitoring_speed_format,
                                        mbps.formatDecimals(2),
                                        item.net.ping
                                    )
                                } else {
                                    stringResource(Res.string.ui_devices_detail_monitoring_offline)
                                }
                                val diskText = getPercentageFree(
                                    isSmall,
                                    item.data.disk.free,
                                    item.data.disk.size
                                )
                                val ramText = getPercentageFree(
                                    isSmall,
                                    item.data.ram.free,
                                    item.data.ram.size
                                )
                                val tempValues = listOfNotNull(
                                    item.data.temp.cpu.toDoubleOrNull(),
                                    item.data.temp.gpu.toDoubleOrNull(),
                                    item.data.temp.bat.toDoubleOrNull(),
                                    item.data.temp.skin.toDoubleOrNull()
                                )
                                val tempText = if (tempValues.isNotEmpty()) {
                                    "${tempValues.average().toInt()} °C"
                                } else {
                                    "-"
                                }

                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(horizontal = 8.dp, vertical = 6.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    if (isSmall) {
                                        Column(modifier = Modifier.weight(2.5f)) {
                                            Text(
                                                timeText,
                                                fontWeight = FontWeight.Light,
                                                style = MaterialTheme.typography.bodyMedium
                                            )
                                            Text(
                                                netText,
                                                fontWeight = FontWeight.Light,
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                    } else {
                                        Text(
                                            timeText,
                                            fontWeight = FontWeight.Light,
                                            modifier = Modifier.weight(1.5f),
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                        Text(
                                            netText,
                                            fontWeight = FontWeight.Light,
                                            modifier = Modifier.weight(2f),
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                    Text(
                                        diskText,
                                        fontWeight = FontWeight.Light,
                                        modifier = Modifier.weight(1f),
                                        style = MaterialTheme.typography.bodyMedium,
                                        textAlign = TextAlign.Center
                                    )
                                    Text(
                                        ramText,
                                        fontWeight = FontWeight.Light,
                                        modifier = Modifier.weight(1f),
                                        style = MaterialTheme.typography.bodyMedium,
                                        textAlign = TextAlign.Center
                                    )
                                    Text(
                                        tempText,
                                        fontWeight = FontWeight.Light,
                                        modifier = Modifier.weight(1f),
                                        style = MaterialTheme.typography.bodyMedium,
                                        textAlign = TextAlign.Center
                                    )
                                }

                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LocalDateTime.formatMonitoringTime(): String {
    val localDateTime =
        this.toInstant(TimeZone.UTC).toLocalDateTime(TimeZone.currentSystemDefault())
    val nowInstant = Clock.System.now()
    val nowLocalDate = nowInstant.toLocalDateTime(TimeZone.currentSystemDefault()).date
    val diffDays = localDateTime.date.daysUntil(nowLocalDate)
    val timeStr = localDateTime.time.toString().take(5) // "HH:MM"
    return when {
        diffDays == 0 -> stringResource(Res.string.ui_devices_detail_monitoring_today, timeStr)
        diffDays == 1 -> stringResource(Res.string.ui_devices_detail_monitoring_yesterday, timeStr)
        diffDays in 2..6 -> {
            val dowRes = when (localDateTime.date.dayOfWeek) {
                DayOfWeek.MONDAY -> Res.string.dow_monday
                DayOfWeek.TUESDAY -> Res.string.dow_tuesday
                DayOfWeek.WEDNESDAY -> Res.string.dow_wednesday
                DayOfWeek.THURSDAY -> Res.string.dow_thursday
                DayOfWeek.FRIDAY -> Res.string.dow_friday
                DayOfWeek.SATURDAY -> Res.string.dow_saturday
                DayOfWeek.SUNDAY -> Res.string.dow_sunday
            }
            val dayName = stringResource(dowRes).lowercase()
                .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
            "$dayName $timeStr"
        }

        else -> {
            val monthRes = when (localDateTime.date.month) {
                Month.JANUARY -> Res.string.month_jan
                Month.FEBRUARY -> Res.string.month_feb
                Month.MARCH -> Res.string.month_mar
                Month.APRIL -> Res.string.month_apr
                Month.MAY -> Res.string.month_may
                Month.JUNE -> Res.string.month_jun
                Month.JULY -> Res.string.month_jul
                Month.AUGUST -> Res.string.month_aug
                Month.SEPTEMBER -> Res.string.month_sep
                Month.OCTOBER -> Res.string.month_oct
                Month.NOVEMBER -> Res.string.month_nov
                Month.DECEMBER -> Res.string.month_dec
            }
            val monthName = stringResource(monthRes)
            val dayStr = localDateTime.date.day.toString().padStart(2, '0')
            "$monthName $dayStr $timeStr"
        }
    }
}

private fun Double.formatDecimals(digits: Int): String {
    val multiplier = 10.0.pow(digits)
    val rounded = round(this * multiplier) / multiplier
    val str = rounded.toString()
    val parts = str.split(".")
    if (parts.size == 1) return "$str." + "0".repeat(digits)
    val decimal = parts[1].padEnd(digits, '0').take(digits)
    return "${parts[0]}.$decimal"
}

@Composable
private fun getPercentageFree(isSmall: Boolean, freeStr: String, sizeStr: String): String {
    val free = freeStr.toDoubleOrNull() ?: return "?"
    val size = sizeStr.toDoubleOrNull() ?: return "?"
    if (size == 0.0) return "?"
    val pct = ((free / size) * 100.0).toInt()
    return if (isSmall) "$pct%" else stringResource(
        Res.string.ui_devices_detail_monitoring_free,
        pct
    )
}

@Composable
context(ctx: DeviceDCtx)
private fun DeviceAppsSection(apps: List<SharedIO.Device.App>) {
    Column(
        modifier = Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = stringResource(Res.string.ui_devices_detail_devicedui_label_default_app),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary
        )
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

        apps.forEach { app ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .border(
                        width = 1.dp,
                        color = MaterialTheme.colorScheme.outlineVariant,
                        shape = RoundedCornerShape(4.dp)
                    )
                    .clip(RoundedCornerShape(4.dp))
                    .clickable {
                        ctx.nav.navTo_AppWheelDUI(ArgsId(app.instanceId))
                    }
                    .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.2f))
                    .padding(16.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    imageVector = Svg.LayersOutline,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(24.dp)
                )
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = app.name.uppercase(),
                        style = MaterialTheme.typography.bodyLarge,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: DeviceDCtx)
private fun AppSelectorDialog() {
    val vm = ctx.vm
    AlertDialog(
        onDismissRequest = { vm.showAppSelectorDialog = false },
        title = {
            Text(
                text = stringResource(Res.string.ui_devices_detail_devicedui_btn_change_default_app),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold
            )
        },
        text = {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(max = 300.dp)
            ) {
                if (vm.isLoadingAppInstances) {
                    CircularProgressIndicator(
                        modifier = Modifier.align(Alignment.Center),
                        color = MaterialTheme.colorScheme.primary
                    )
                } else if (vm.appInstancesList.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "No app instances found",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                } else {
                    androidx.compose.foundation.lazy.LazyColumn(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        items(vm.appInstancesList.size) { index ->
                            val appInstance = vm.appInstancesList[index]
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .border(
                                        width = 1.dp,
                                        color = MaterialTheme.colorScheme.outlineVariant,
                                        shape = RoundedCornerShape(4.dp)
                                    )
                                    .clip(RoundedCornerShape(4.dp))
                                    .clickable {
                                        vm.setDefaultApp(appInstance.app.type, appInstance.id)
                                    }
                                    .padding(12.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = appInstance.name.uppercase(),
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontWeight = FontWeight.Bold
                                )
                                val appType = ApplicationType[appInstance.app.type]
                                val typeLabel =
                                    if (appType != null) stringResource(appType.label) else ""
                                Text(
                                    text = typeLabel,
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.primary,
                                    fontWeight = FontWeight.SemiBold
                                )
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = {
                    vm.setDefaultApp(ApplicationType.WWW_CAMPAIGN.mask, -1L)
                }
            ) {
                Text(
                    text = "CAMPAIGNS",
                    color = MaterialTheme.colorScheme.primary,
                    fontWeight = FontWeight.Bold
                )
            }
        },
        dismissButton = {
            TextButton(
                onClick = {
                    vm.showAppSelectorDialog = false
                }
            ) {
                Text(
                    text = stringResource(Res.string.ui_devices_detail_devicedui_btn_cancel),
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    )
}

